import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Api, createUri } from '@api/utils';
import { BannerScope, BannerUrls } from '@api/banners';
import { Mutation, Query } from './utils';

import type {
    BannerListItem,
    BannersBulkRequest,
    BannersResponse,
} from '@api/banners';

enum Tag {
    Banners = 'banners',
}

const tagsFor = (scope: BannerScope, companyId?: number) => [
    {
        type: Tag.Banners,
        id: scope === BannerScope.Global ? 'global' : `${scope}:${companyId}`,
    },
];

export const bannerHooks = createApi({
    reducerPath: 'bannerApi',
    tagTypes: Object.values(Tag),
    baseQuery: fetchBaseQuery({
        baseUrl: createUri(Api.Orgs, '/org/V1'),
    }),
    endpoints: (build) => ({
        banners: build.query<
            BannerListItem[],
            { token: string; scope: BannerScope; companyId?: number }
        >({
            query: ({ token, scope, companyId }) => ({
                url: BannerUrls.Banners,
                params:
                    scope === BannerScope.Global
                        ? { scope }
                        : { scope, companyId },
                headers: { Authorization: `Bearer ${token}` },
            }),
            transformResponse: (response: BannersResponse) => response.banners,
            providesTags: (_data, _error, { scope, companyId }) =>
                tagsFor(scope, companyId),
        }),
        bannersBulk: build.mutation<
            BannerListItem[],
            { token: string; payload: BannersBulkRequest }
        >({
            query: ({ token, payload }) => ({
                url: BannerUrls.Banners,
                method: 'PUT',
                body: payload,
                headers: { Authorization: `Bearer ${token}` },
            }),
            transformResponse: (response: BannersResponse) => response.banners,
            invalidatesTags: (_data, _error, { payload: { scope, companyId } }) =>
                tagsFor(scope, companyId),
        }),
    }),
});

const banners = {
    useBannersQuery: Query.withToken(bannerHooks.useBannersQuery),
    useBannersBulkMutation: Mutation.withToken(bannerHooks.useBannersBulkMutation),
};

export const { useBannersQuery, useBannersBulkMutation } = banners;
